import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, Check, AlertCircle, Loader2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const RESET_TOKENS_KEY = 'votebeats_reset_tokens';
const USERS_KEY = 'votebeats_mock_users';

interface TokenData {
  email: string;
  createdAt: number;
  expiresAt: number;
}

function getTokenData(token: string): TokenData | null {
  const tokens = JSON.parse(localStorage.getItem(RESET_TOKENS_KEY) || '{}');
  return tokens[token] || null;
}

function invalidateToken(token: string) {
  const tokens = JSON.parse(localStorage.getItem(RESET_TOKENS_KEY) || '{}');
  delete tokens[token];
  localStorage.setItem(RESET_TOKENS_KEY, JSON.stringify(tokens));
}

function updateUserPassword(email: string, newPassword: string) {
  const users = JSON.parse(localStorage.getItem(USERS_KEY) || '{}');
  if (users[email]) {
    users[email].password = newPassword;
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
    return true;
  }
  return false;
}

// Password validation helpers
function validatePassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('At least 8 characters');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('One number');
  }

  return { valid: errors.length === 0, errors };
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenEmail, setTokenEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validation = validatePassword(password);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    const tokenData = getTokenData(token);
    if (!tokenData) {
      setTokenValid(false);
      return;
    }

    // Check if token is expired
    if (Date.now() > tokenData.expiresAt) {
      setTokenValid(false);
      invalidateToken(token);
      return;
    }

    setTokenValid(true);
    setTokenEmail(tokenData.email);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (!validation.valid) {
      setError('Password does not meet requirements');
      return;
    }

    // Check password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update the password
      const updated = updateUserPassword(tokenEmail, password);
      if (!updated) {
        throw new Error('Failed to update password. User not found.');
      }

      // Invalidate the token
      if (token) {
        invalidateToken(token);
      }

      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking token
  if (tokenValid === null) {
    return (
      <div className="space-y-6 text-center">
        <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
        <p className="text-gray-600 dark:text-gray-400">Validating reset link...</p>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
          <XCircle className="w-8 h-8 text-danger" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invalid Reset Link</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This password reset link is invalid or has expired.
          </p>
        </div>
        <Link to="/forgot-password" className="btn btn-primary inline-flex items-center">
          Request New Reset Link
        </Link>
        <p className="text-center">
          <Link to="/" className="text-sm text-primary hover:text-primary-hover inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Sign In
          </Link>
        </p>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
          <Check className="w-8 h-8 text-success" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Password Reset!</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="btn btn-primary inline-flex items-center"
        >
          Sign In Now
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Set New Password</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Create a new password for <strong>{tokenEmail}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input pl-10"
              required
              disabled={loading}
            />
          </div>
          {/* Password requirements */}
          {password && (
            <ul className="mt-2 text-sm space-y-1">
              {['At least 8 characters', 'One uppercase letter', 'One lowercase letter', 'One number'].map((req) => {
                const passed = !validation.errors.includes(req);
                return (
                  <li key={req} className={`flex items-center gap-1 ${passed ? 'text-success' : 'text-gray-400'}`}>
                    {passed ? <Check className="w-4 h-4" /> : <span className="w-4 h-4 inline-block text-center">○</span>}
                    {req}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Confirm New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="input pl-10"
              required
              disabled={loading}
            />
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="mt-1 text-sm text-danger">Passwords do not match</p>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary w-full"
          disabled={loading || !validation.valid || password !== confirmPassword}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <Check className="w-5 h-5 mr-2" />
              Reset Password
            </>
          )}
        </button>
      </form>

      <p className="text-center">
        <Link to="/" className="text-sm text-primary hover:text-primary-hover inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Sign In
        </Link>
      </p>
    </div>
  );
}
